/* @ts-ignore */
import React, { useEffect, useState } from 'react'
import GenericModal from 'src/components/modals/generic-modal'
import { useAuthService } from 'theme/services/auth/auth-service'
import { groupBy } from 'lodash-es'
import { navigate } from 'gatsby'
import { getConferenceLocation, getEventLink } from 'theme/utils/generic-helpers'
import ApiGateway from 'theme/api/api-gateway'
import * as Sentry from '@sentry/gatsby'
import ShortUniqueId from 'short-unique-id'
import {
  Details,
  PurchaseTicketsProps,
  TempTicket,
} from 'src/components/modals/purchase-tickets/purchase-tickets.interfaces'
import { StripeInputDetails, StripeInvoiceDetails, StripeCustomer } from 'theme/api/api-gateway.interfaces'
import { MailIcon } from '@heroicons/react/outline'
import { CountryDropdown } from 'react-country-region-selector'

const discount5for4 = 0.8

const taxIdTypes = [
  {
    code: 'eu_vat',
    name: 'EU VAT',
    flag: '🇪🇺',
    placeholder: '',
    id: 'eu_vat',
  },
  {
    code: 'eu_vat',
    name: 'AT VAT',
    flag: '🇦🇹',
    placeholder: 'ATU12345678',
    id: 'at_vat',
  },
  {
    code: 'au_abn',
    name: 'AU ABN',
    flag: '🇦🇺',
    placeholder: '12345678912',
    id: 'au_abn',
  },
  {
    code: 'au_arn',
    name: 'AU ARN',
    flag: '🇦🇺',
    placeholder: '123456789123',
    id: 'au_arn',
  },
  {
    code: 'eu_vat',
    name: 'BE VAT',
    flag: '🇧🇪',
    placeholder: 'BE0123456789',
    id: 'be_vat',
  },
  {
    code: 'bg_uic',
    name: 'BG UIC',
    flag: '🇧🇬',
    placeholder: '123456789',
    id: 'bg_uic',
  },
  {
    code: 'eu_vat',
    name: 'BG VAT',
    flag: '🇧🇬',
    placeholder: 'BG0123456789',
    id: 'bg_vat',
  },
  {
    code: 'br_cnpj',
    name: 'BR CNPJ',
    flag: '🇧🇷',
    placeholder: '01.234.456/5432-10',
    id: 'br_cnpj',
  },
  {
    code: 'br_cpf',
    name: 'BR CPF',
    flag: '🇧🇷',
    placeholder: '123.456.789-87',
    id: 'br_cpf',
  },
  {
    code: 'ca_bn',
    name: 'CA BN',
    flag: '🇨🇦',
    placeholder: '123456789',
    id: 'ca_bn',
  },
  {
    code: 'ca_gst_hst',
    name: 'CA GST/HST',
    flag: '🇨🇦',
    placeholder: '123456789RT0002',
    id: 'ca_gst_hst',
  },
  {
    code: 'ca_pst_bc',
    name: 'CA PST (BC)',
    flag: '🇨🇦',
    placeholder: 'PST-1234-5678',
    id: 'ca_pst_bc',
  },
  {
    code: 'ca_pst_mb',
    name: 'CA PST (MB)',
    flag: '🇨🇦',
    placeholder: '123456-7',
    id: 'ca_pst_mb',
  },
  {
    code: 'ca_pst_sk',
    name: 'CA PST (SK)',
    flag: '🇨🇦',
    placeholder: '1234567',
    id: 'ca_pst_sk',
  },
  {
    code: 'ca_qst',
    name: 'CA QST',
    flag: '🇨🇦',
    placeholder: '1234567890TQ1234',
    id: 'ca_qst',
  },
  {
    code: 'ch_vat',
    name: 'CH VAT',
    flag: '🇨🇭',
    placeholder: 'CHE-123.456.789 MWST',
    id: 'ch_vat',
  },
  {
    code: 'cl_tin',
    name: 'CL TIN',
    flag: '🇨🇱',
    placeholder: '12.345.678-K',
    id: 'cl_tin',
  },
  {
    code: 'eu_vat',
    name: 'CY VAT',
    flag: '🇨🇾',
    placeholder: 'CY12345678Z',
    id: 'cy_vat',
  },
  {
    code: 'eu_vat',
    name: 'CZ VAT',
    flag: '🇨🇿',
    placeholder: 'CZ1234567890',
    id: 'cz_vat',
  },
  {
    code: 'eu_vat',
    name: 'DE VAT',
    flag: '🇩🇪',
    placeholder: 'DE123456789',
    id: 'de_vat',
  },
  {
    code: 'eu_vat',
    name: 'DK VAT',
    flag: '🇩🇰',
    placeholder: 'DK12345678',
    id: 'dk_vat',
  },
  {
    code: 'eu_vat',
    name: 'EE VAT',
    flag: '🇪🇪',
    placeholder: 'EE123456789',
    id: 'ee_vat',
  },
  {
    code: 'es_cif',
    name: 'ES CIF',
    flag: '🇪🇸',
    placeholder: 'A12345678',
    id: 'es_cif',
  },
  {
    code: 'eu_vat',
    name: 'ES VAT',
    flag: '🇪🇸',
    placeholder: 'ESA1234567Z',
    id: 'es_vat',
  },
  {
    code: 'eu_oss_vat',
    name: 'EU OSS_VAT',
    flag: '🇪🇺',
    placeholder: 'EU123456789',
    id: 'eu_oss_vat',
  },
  {
    code: 'eu_vat',
    name: 'FI VAT',
    flag: '🇫🇮',
    placeholder: 'FI12345678',
    id: 'fi_vat',
  },
  {
    code: 'eu_vat',
    name: 'FR VAT',
    flag: '🇫🇷',
    placeholder: 'FRAB123456789',
    id: 'fr_vat',
  },
  {
    code: 'gb_vat',
    name: 'GB VAT',
    flag: '🇬🇧',
    placeholder: 'GB123456789',
    id: 'gb_vat',
  },
  {
    code: 'ge_vat',
    name: 'GE VAT',
    flag: '🇬🇪',
    placeholder: '123456789',
    id: 'ge_vat',
  },
  {
    code: 'hk_br',
    name: 'HK BR',
    flag: '🇭🇰',
    placeholder: '12345678',
    id: 'hk_br',
  },
  {
    code: 'eu_vat',
    name: 'HR VAT',
    flag: '🇭🇷',
    placeholder: 'HR12345678912',
    id: 'hr_vat',
  },
  {
    code: 'hu_tin',
    name: 'HU TIN',
    flag: '🇭🇺',
    placeholder: '12345678-1-23',
    id: 'hu_tin',
  },
  {
    code: 'eu_vat',
    name: 'HU VAT',
    flag: '🇭🇺',
    placeholder: 'HU12345678912',
    id: 'hu_vat',
  },
  {
    code: 'id_npwp',
    name: 'ID NPWP',
    flag: '🇮🇩',
    placeholder: '12.345.678.9-012.345',
    id: 'id_npwp',
  },
  {
    code: 'eu_vat',
    name: 'IE VAT',
    flag: '🇮🇪',
    placeholder: 'IE1234567AB',
    id: 'ie_vat',
  },
  {
    code: 'il_vat',
    name: 'IL VAT',
    flag: '🇮🇱',
    placeholder: '000012345',
    id: 'il_vat',
  },
  {
    code: 'in_gst',
    name: 'IN GST',
    flag: '🇮🇳',
    placeholder: '12ABCDE3456FGZH',
    id: 'in_gst',
  },
  {
    code: 'is_vat',
    name: 'IS VAT',
    flag: '🇮🇸',
    placeholder: '123456',
    id: 'is_vat',
  },
  {
    code: 'eu_vat',
    name: 'IT VAT',
    flag: '🇮🇹',
    placeholder: 'IT12345678912',
    id: 'it_vat',
  },
  {
    code: 'jp_cn',
    name: 'JP CN',
    flag: '🇯🇵',
    placeholder: '1234567891234',
    id: 'jp_cn',
  },
  {
    code: 'jp_rn',
    name: 'JP RN',
    flag: '🇯🇵',
    placeholder: '12345',
    id: 'jp_rn',
  },
  {
    code: 'kr_brn',
    name: 'KR BRN',
    flag: '🇰🇷',
    placeholder: '123-45-67890',
    id: 'kr_brn',
  },
  {
    code: 'li_uid',
    name: 'LI UID',
    flag: '🇱🇮',
    placeholder: 'CHE123456789',
    id: 'li_uid',
  },
  {
    code: 'eu_vat',
    name: 'LT VAT',
    flag: '🇱🇹',
    placeholder: 'LT123456789123',
    id: 'lt_vat',
  },
  {
    code: 'eu_vat',
    name: 'LU VAT',
    flag: '🇱🇺',
    placeholder: 'LU12345678',
    id: 'lu_vat',
  },
  {
    code: 'eu_vat',
    name: 'LV VAT',
    flag: '🇱🇻',
    placeholder: 'LV12345678912',
    id: 'lv_vat',
  },
  {
    code: 'eu_vat',
    name: 'MT VAT',
    flag: '🇲🇹',
    placeholder: 'MT12345678',
    id: 'mt_vat',
  },
  {
    code: 'mx_rfc',
    name: 'MX RFC',
    flag: '🇲🇽',
    placeholder: 'ABC010203AB9',
    id: 'mx_rfc',
  },
  {
    code: 'my_frp',
    name: 'MY FRP',
    flag: '🇲🇾',
    placeholder: '12345678',
    id: 'my_frp',
  },
  {
    code: 'my_itn',
    name: 'MY ITN',
    flag: '🇲🇾',
    placeholder: 'C 1234567890',
    id: 'my_itn',
  },
  {
    code: 'my_sst',
    name: 'MY SST',
    flag: '🇲🇾',
    placeholder: 'A12-3456-78912345',
    id: 'my_sst',
  },
  {
    code: 'eu_vat',
    name: 'NL VAT',
    flag: '🇳🇱',
    placeholder: 'NL123456789B12',
    id: 'nl_vat',
  },
  {
    code: 'no_vat',
    name: 'NO VAT',
    flag: '🇳🇴',
    placeholder: '123456789MVA',
    id: 'no_vat',
  },
  {
    code: 'nz_gst',
    name: 'NZ GST',
    flag: '🇳🇿',
    placeholder: '123456789',
    id: 'nz_gst',
  },
  {
    code: 'eu_vat',
    name: 'PL VAT',
    flag: '🇵🇱',
    placeholder: 'PL1234567890',
    id: 'pl_vat',
  },
  {
    code: 'eu_vat',
    name: 'PT VAT',
    flag: '🇵🇹',
    placeholder: 'PT123456789',
    id: 'pt_vat',
  },
  {
    code: 'eu_vat',
    name: 'RO VAT',
    flag: '🇷🇴',
    placeholder: 'RO1234567891',
    id: 'ro_vat',
  },
  {
    code: 'ru_inn',
    name: 'RU INN',
    flag: '🇷🇺',
    placeholder: '1234567891',
    id: 'ru_inn',
  },
  {
    code: 'ru_kpp',
    name: 'RU KPP',
    flag: '🇷🇺',
    placeholder: '123456789',
    id: 'ru_kpp',
  },
  {
    code: 'sa_vat',
    name: 'SA VAT',
    flag: '🇸🇦',
    placeholder: '123456789012345',
    id: 'sa_vat',
  },
  {
    code: 'eu_vat',
    name: 'SE VAT',
    flag: '🇸🇪',
    placeholder: 'SE123456789123',
    id: 'se_vat',
  },
  {
    code: 'sg_gst',
    name: 'SG GST',
    flag: '🇸🇬',
    placeholder: 'M12345678X',
    id: 'sg_gst',
  },
  {
    code: 'sg_uen',
    name: 'SG UEN',
    flag: '🇸🇬',
    placeholder: '123456789F',
    id: 'sg_uen',
  },
  {
    code: 'si_tin',
    name: 'SI TIN',
    flag: '🇸🇮',
    placeholder: '12345678',
    id: 'si_tin',
  },
  {
    code: 'eu_vat',
    name: 'SI VAT',
    flag: '🇸🇮',
    placeholder: 'SI12345678',
    id: 'si_vat',
  },
  {
    code: 'eu_vat',
    name: 'SK VAT',
    flag: '🇸🇰',
    placeholder: 'SK1234567891',
    id: 'sk_vat',
  },
  {
    code: 'th_vat',
    name: 'TH VAT',
    flag: '🇹🇭',
    placeholder: '1234567891234',
    id: 'th_vat',
  },
  {
    code: 'tw_vat',
    name: 'TW VAT',
    flag: '🇹🇼',
    placeholder: '12345678',
    id: 'tw_vat',
  },
  {
    code: 'ua_vat',
    name: 'UA VAT',
    flag: '🇺🇦',
    placeholder: '123456789',
    id: 'ua_vat',
  },
  {
    code: 'us_ein',
    name: 'US EIN',
    flag: '🇺🇸',
    placeholder: '12-3456789',
    id: 'us_ein',
  },
  {
    code: 'za_vat',
    name: 'ZA VAT',
    flag: '🇿🇦',
    placeholder: '4123456789',
    id: 'za_vat',
  },
]
const uid = new ShortUniqueId({
  dictionary: 'alphanum_upper',
  length: 8,
})

// sort by taxIdTypes

// import { StringChain } from 'lodash'

const groupTicketsByType = (tickets) => {
  const flattedTickets = tickets.map(({ id, data }) => {
    const delivery = data.Delivery?.[0] ?? 'Uncategorized'
    const name = data.Access?.[0] ?? ''
    const details = { ...data }
    delete details.Delivery
    delete details.Access

    return {
      id,
      delivery,
      name,
      ...details,
    }
  })

  return groupBy(flattedTickets, 'delivery')
}

declare global {
  interface Window {
    dataLayer: any
  }
}

const PurchaseTickets = ({
  isVisible,
  onClose,
  conference,
  allTickets,
  selectedTicketId,
}: PurchaseTicketsProps) => {
  const newsletterObj = {
    emea: 20,
    usa: 21,
    apac: 19,
  }
  // console.log(conference.data)
  const sibIds = [6, newsletterObj[conference.data.Region?.toLowerCase()]].filter((n) => n)

  const conferenceLocation = getConferenceLocation(conference)
  const authService = useAuthService()
  const user = authService.user
  const groupedTickets = groupTicketsByType(allTickets)
  const [subscribe, setSubscribe] = useState(true)
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const [discount, setDiscount] = useState('')
  const [hideDiscount, setHideDiscount] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState('')
  const [isRequestingInvoice, setIsRequestingInvoice] = useState('')
  const [showBillingInfo, setShowBillingInfo] = useState(false)
  const [billingCity, setBillingCity] = useState('')
  const [billingCountry, setBillingCountry] = useState('')
  const [billingLine1, setBillingLine1] = useState('')
  const [billingLine2, setBillingLine2] = useState('')
  const [billingPostalCode, setBillingPostalCode] = useState('')
  const [billingState, setBillingState] = useState('')
  const [billingName, setBillingName] = useState('')
  const [billingTaxPlaceholder, setBillingTaxPlaceholder] = useState('')
  const [billingTaxId, setBillingTaxId] = useState('')
  const [billingPO, setBillingPO] = useState('')
  const [billingTaxType, setBillingTaxType] = useState('')
  const [originalCustomerDetails, setOriginalCustomerDetails] = useState({} as StripeCustomer)

  // console.log(allTickets)
  const getPlaceholder = (taxId) => {
    const fitleredTaxType = taxIdTypes.filter((taxIdType) => {
      return taxIdType.id === taxId
    })
    setBillingTaxPlaceholder(fitleredTaxType[0]?.placeholder)
  }

  const initialQuantity = allTickets.reduce(
    (acc, cur) => ({
      ...acc,
      [cur.id]: cur.id === selectedTicketId ? 1 : 0,
    }),
    {},
  )
  const initialDiscount = allTickets.reduce(
    (acc, cur) => ({
      ...acc,
      [cur.id]: 1,
    }),
    {},
  )
  const initialPrice = allTickets
    .filter((ticket) => initialQuantity[ticket.id] > 0)
    .map((ticket) => ({
      ...ticket,
      quantity: initialQuantity[ticket.id],
    }))
    .reduce((acc, cur) => acc + cur.quantity * cur.data.Price, 0)

  const selectedTicket = allTickets.filter((ticket) => ticket.id === selectedTicketId)[0]
  const currency = selectedTicket.data.Currency
  const initialTax =
    selectedTicket.data.Delivery[0] === 'In-Person' && selectedTicket.data.Tax_Rates?.[0]?.data?.Rate
      ? (selectedTicket.data.Tax_Rates[0].data.Rate * initialPrice) / 100
      : 0
  const [totalPrice, setTotalPrice] = useState(initialPrice)
  const [quantity, setQuantity] = useState(initialQuantity)
  const [tax, setTax] = useState(initialTax)
  const [discounts, setDiscounts] = useState(initialDiscount)

  let env = conference.data.Region + 'prod',
    prefix = ''
  if (conference.data.Organisation !== 'UXDX') {
    env = conference.data.Region + 'dev'
    prefix = 'TEST-'
  }

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  const increaseTicketQuantity = (id) => () => {
    if (window) {
      // @ts-ignore: Unreachable code error
      window.dataLayer = window.dataLayer || []

      // clear the dataLayer
      window.dataLayer.push({ ecommerce: null })

      // @ts-ignore: Unreachable code error
      const selectedTicket = allTickets.find((ticket) => ticket.id === id)
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          items: [
            {
              item_id: selectedTicket?.data?.Stripe_Price_Id,
              item_name: selectedTicket?.data?.Name,
              item_category: selectedTicket?.data?.Delivery[0],
              item_category2:
                conference.data.Conference_Id?.indexOf('community') !== -1 ? 'community' : 'conference',
              item_category3: conference.data.Conference_Id,
              item_category4: conference.data.Organisation,
              item_variant: selectedTicket?.data?.Access[0],
              price: selectedTicket?.data?.Price,
              quantity: 1,
            },
          ],
        },
      })
    }
    const newQuantity = {
      ...quantity,
      [id]: quantity[id] + 1,
    }
    const selectedTickets = allTickets
      .filter((ticket) => newQuantity[ticket.id] > 0)
      .map((ticket) => ({
        ...ticket,
        quantity: newQuantity[ticket.id],
      }))

    // check if eligible for discount
    const discountMultiplier = newQuantity[id] >= 5 ? discount5for4 : 1
    const updatedDiscounts = { ...discounts, [id]: discountMultiplier }
    const totalPrice = selectedTickets.reduce(
      (acc, cur) => acc + cur.quantity * cur.data.Price * updatedDiscounts[cur.id],
      0,
    )
    if (selectedTicket.data.Tax_Rates?.[0]?.data?.Rate && selectedTicket.data.Delivery[0] === 'In-Person')
      setTax((totalPrice * selectedTicket.data.Tax_Rates[0].data.Rate) / 100)
    setTotalPrice(totalPrice)
    setDiscounts(updatedDiscounts)
    // update the totalPrice
    return setQuantity(newQuantity)
  }

  const decreaseTicketQuantity = (id) => () => {
    if (window) {
      // @ts-ignore: Unreachable code error
      window.dataLayer = window.dataLayer || []

      // clear the dataLayer
      window.dataLayer.push({ ecommerce: null })

      // @ts-ignore: Unreachable code error
      const selectedTicket = allTickets.find((ticket) => ticket.id === id)
      window.dataLayer.push({
        event: 'remove_from_cart',
        ecommerce: {
          items: [
            {
              item_id: selectedTicket?.data?.Stripe_Price_Id,
              item_name: selectedTicket?.data?.Name,
              item_category: selectedTicket?.data?.Delivery[0],
              item_category2:
                conference.data.Conference_Id?.indexOf('community') !== -1 ? 'community' : 'conference',
              item_category3: conference.data.Conference_Id,
              item_category4: conference.data.Organisation,
              item_variant: selectedTicket?.data?.Access[0],
              price: selectedTicket?.data?.Price,
              quantity: 1,
            },
          ],
        },
      })
    }
    const newQuantity = {
      ...quantity,
      [id]: quantity[id] > 0 ? quantity[id] - 1 : 0,
    }
    const discountMultiplier = newQuantity[id] >= 5 ? discount5for4 : 1
    const selectedTickets = allTickets
      .filter((ticket) => newQuantity[ticket.id] > 0)
      .map((ticket) => ({
        ...ticket,
        quantity: newQuantity[ticket.id],
      }))

    const updatedDiscounts = { ...discounts, [id]: discountMultiplier }
    const totalPrice = selectedTickets.reduce(
      (acc, cur) => acc + cur.quantity * cur.data.Price * updatedDiscounts[cur.id],
      0,
    )
    if (selectedTicket.data.Tax_Rates?.[0]?.data?.Rate && selectedTicket.data.Delivery[0] === 'In-Person')
      setTax((totalPrice * selectedTicket.data.Tax_Rates[0].data.Rate) / 100)
    setTotalPrice(totalPrice)
    setDiscounts(updatedDiscounts)

    // update the totalPrice
    return setQuantity(newQuantity)
  }

  const getBillingInfo = async () => {
    setError('')
    const stripeCustomerId = user
      ? env.indexOf('prod') !== -1
        ? user[conference.data.Region + 'stripeCustomerId']
        : user[conference.data.Region + 'stripeCustomerIdTest']
      : null
    if (stripeCustomerId) {
      setIsSubmitting('Looking up previous details...')
      const timer1 = setTimeout(() => setIsSubmitting('Populating information from Stripe...'), 3500)
      const { customer } = await ApiGateway.getStripeCustomer(stripeCustomerId, env)

      clearTimeout(timer1)
      // set all null values to empty strings
      setOriginalCustomerDetails(customer)
      if (customer) {
        Object.keys(customer.address).forEach((key) => {
          if (!customer.address[key]) customer.address[key] = ''
        })
        // set the billingDetails
        if (customer.address && customer.address.city) setBillingCity(customer.address.city)
        if (customer.address && customer.address.country) setBillingCountry(customer.address.country)
        if (customer.address && customer.address.line1) setBillingLine1(customer.address.line1)
        if (customer.address && customer.address.line2) setBillingLine2(customer.address.line2)
        if (customer.address && customer.address.postal_code)
          setBillingPostalCode(customer.address.postal_code)
        if (customer.address && customer.address.state) setBillingState(customer.address.state)
        if (customer.name) setBillingName(customer.name)
        if (customer.tax_ids && customer.tax_ids.data && customer.tax_ids.data[0]) {
          setBillingTaxId(customer.tax_ids.data[0].value)
          setBillingTaxType(customer.tax_ids.data[0].type)
        }
      }
    }
    setIsSubmitting('')
    setShowBillingInfo(true)
  }

  const handleInvoiceRequest = async (e) => {
    e.preventDefault()

    const orderId = prefix + uid()

    const selectedTickets = allTickets
      .filter((ticket) => quantity[ticket.id] > 0)
      .map((ticket) => ({
        ...ticket,
        quantity: quantity[ticket.id],
      }))

    if (!selectedTickets.length) {
      setIsSubmitting('')
      setError('You must specify a quantity for at least one ticket')
      return
    }

    if (window) {
      // @ts-ignore: Unreachable code error
      window.dataLayer = window.dataLayer || []

      // clear the dataLayer
      window.dataLayer.push({ ecommerce: null })

      // @ts-ignore: Unreachable code error
      window.dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          transaction_id: orderId,
          value: totalPrice,
          currency: selectedTickets[0].data.Currency,
          coupon: discount && discount.length > 0 ? discount : null,
          items: selectedTickets.map((ticket) => {
            return {
              item_id: ticket.data.Stripe_Price_Id,
              item_name: ticket.data.Name,
              item_category: ticket.data.Delivery[0],
              item_category2:
                conference.data.Conference_Id?.indexOf('community') !== -1 ? 'community' : 'conference',
              item_category3: conference.data.Conference_Id,
              item_category4: conference.data.Organisation,
              item_variant: ticket.data.Access[0],
              price: ticket.data.Price,
              quantity: ticket.quantity,
            }
          }),
        },
      })
    }

    const invoiceDetails: StripeInvoiceDetails = {
      metadata: {
        orderId,
        productType: selectedTicket.data.Product_Type,
        productId: selectedTicket.data.UXDX_Product_Id,
        productName: selectedTicket.data.Product_Name,
        startDate: selectedTicket.data.Product_Start_Date,
        organisation: conference.data.Organisation,
        productTaxCountry: conference.data.ProductTaxCountry?.[0],
        email: (user && user.email) || email,
        uid: user && user.uid,
        sendInBlueIds: [selectedTicket.data.SendinBlue_Ticket_Id, selectedTicket.data.SendinBlue_Id].join(
          ',',
        ),
        subscribe: subscribe ? sibIds.join(',') : false,
        sibIds: sibIds.join(','),
        billingPO: billingPO,
      },
      collection_method: 'send_invoice',
      days_until_due: 30,
    }
    if (
      selectedTicket.data.Delivery[0] === 'Online' ||
      (!selectedTicket.data.Stripe_Tax_Id && !selectedTicket.data.No_Tax[0])
    ) {
      invoiceDetails.automatic_tax = {
        enabled: true,
      }
    } else if (selectedTicket.data.Stripe_Tax_Id) {
      invoiceDetails.default_tax_rates =
        env.indexOf('prod') !== -1
          ? selectedTicket.data.Stripe_Tax_Id
          : selectedTicket.data.Stripe_Tax_Id__Test_
    }
    // remove null values from invoiceDetails
    const stripeCustomerId = user
      ? env.indexOf('prod') !== -1
        ? user[conference.data.Region + 'stripeCustomerId']
        : user[conference.data.Region + 'stripeCustomerIdTest']
      : null
    if (user && stripeCustomerId) {
      invoiceDetails.customer = stripeCustomerId
    }

    if (discount && discount.length > 0) {
      invoiceDetails.discounts = [{ promotion_code: discount }]
    }

    const customer: StripeCustomer = {
      name: billingName,
      address: {
        city: billingCity,
        country: billingCountry,
        line1: billingLine1,
        line2: billingLine2,
        postal_code: billingPostalCode,
        state: billingState,
      },
      tax_ids: {
        data: [
          {
            value: billingTaxId,
            type: taxIdTypes.find((taxIdType) => taxIdType.id === billingTaxType)?.code,
          },
        ],
      },
    }

    // check if billingInfo is different to originalDetails
    const updatedCustomerInfo =
      originalCustomerDetails &&
      (billingName !== originalCustomerDetails.name ||
        billingCity !== originalCustomerDetails.address?.city ||
        billingCountry !== originalCustomerDetails.address?.country ||
        billingLine1 !== originalCustomerDetails.address?.line1 ||
        billingLine2 !== originalCustomerDetails.address?.line2 ||
        billingPostalCode !== originalCustomerDetails.address?.postal_code ||
        billingState !== originalCustomerDetails.address?.state)

    const updatedCustomerTaxIds =
      originalCustomerDetails &&
      (billingTaxId != (originalCustomerDetails.tax_ids?.data?.[0]?.value || '') ||
        billingTaxType != (originalCustomerDetails.tax_ids?.data?.[0]?.type || ''))

    const lineItems = selectedTickets.map((ticket) => {
      const isEligibleForDiscount = quantity[ticket.id] >= 5
      return {
        quantity: quantity[ticket.id],
        price:
          env.indexOf('prod') !== -1
            ? isEligibleForDiscount
              ? ticket.data.Stripe_Reduced_Price_Id
              : ticket.data.Stripe_Price_Id
            : isEligibleForDiscount
              ? ticket.data.Stripe_Reduced_Price_Id__Test_
              : ticket.data.Stripe_Price_Id__Test_,
      }
    })

    try {
      // else ask them to pay
      setIsRequestingInvoice('Validating your order')
      const timer1 = setTimeout(() => setIsRequestingInvoice('Creating your invoice'), 3500)
      const timer2 = setTimeout(() => setIsRequestingInvoice('Emailing your invoice'), 8000)
      const details = await ApiGateway.requestStripeInvoice(
        invoiceDetails,
        lineItems,
        customer,
        updatedCustomerInfo,
        updatedCustomerTaxIds,
        env,
      )

      clearTimeout(timer1)
      clearTimeout(timer2)
      if (details.invoice) {
        setIsRequestingInvoice('')
        // navigate to the success page
        navigate(
          `${getEventLink(conference.data.Conference_Id, conferenceLocation)}/success?env=${env}&confId=${
            conference.data.Conference_Id
          }&invoice_id=${details.invoice.id}&order_id=${orderId}&organisation=${
            conference.data.Organisation
          }`,
        )
      } else if (details.code === 'tax_id_invalid') {
        // bad tax code
        setIsRequestingInvoice('')
        setError('Invalid tax code. Please update the code.')
        // } else if (details.error && details.error.raw.message.indexOf('Invalid type') !== -1) {
        //   // bad tax code
        //   setIsRequestingInvoice('')
        //   setError('Invalid tax code type. Please update the code type.')
        // } else if (details.error && details.error.code === 'parameter_invalid_empty') {
        //   // bad tax code
        //   setIsRequestingInvoice('')
        //   setError('Invalid tax code. Please ensure both a tax type and code are entered.')
      } else if (details.code === 'invalid_discount') {
        // bad tax code
        setIsRequestingInvoice('')
        setError('Invalid discount code. Please enter a valid code.')
      } else {
        setIsRequestingInvoice('')
        setError('Something went wrong. If this happens again please let us know in the chat.')
        Sentry.captureException(new Error('Stripe Invoice failed' + JSON.stringify(details)))
      }
    } catch (e) {
      setIsRequestingInvoice('')
      Sentry.captureException(e)
      setError('Something has gone wrong. If this happens again please let us know in the chat.')
    }
  }

  const onBuyClickHandler = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    const selectedTickets = allTickets
      .filter((ticket) => quantity[ticket.id] > 0)
      .map((ticket) => ({
        ...ticket,
        quantity: quantity[ticket.id],
      }))

    if (!selectedTickets.length) {
      setIsSubmitting('')
      setError('You must specify a quantity for at least one ticket')
      return
    }
    // check if the person is requesting an invoice and if they have a customer
    setIsSubmitting('Validating Order')
    const timer1 = setTimeout(() => setIsSubmitting('Creating Order'), 3500)
    const orderId = prefix + uid()

    const tickets = selectedTickets
      .map((ticket) => {
        const tempTickets: TempTicket[] = []
        for (let i = 0; i < ticket.quantity; i++) {
          tempTickets.push({
            ...ticket,
            type: 'Attendee',
          })
        }
        return tempTickets
      })
      .flat()

    if (window) {
      // @ts-ignore: Unreachable code error
      window.dataLayer = window.dataLayer || []

      // clear the dataLayer
      window.dataLayer.push({ ecommerce: null })

      // @ts-ignore: Unreachable code error
      window.dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          transaction_id: orderId,
          value: totalPrice,
          currency: selectedTickets[0].data.Currency,
          coupon: null,
          items: selectedTickets.map((ticket) => {
            return {
              item_id: ticket.data.Stripe_Price_Id,
              item_name: ticket.data.Name,
              item_category: ticket.data.Delivery[0],
              item_category2:
                conference.data.Conference_Id?.indexOf('community') !== -1 ? 'community' : 'conference',
              item_category3: conference.data.Conference_Id,
              item_category4: conference.data.Organisation,
              item_variant: ticket.data.Access[0],
              price: ticket.data.Price,
              quantity: ticket.quantity,
            }
          }),
        },
      })
    }

    const requestData = {
      tickets,
      selectedTickets,
      productType: selectedTicket.data.Product_Type,
      productId: selectedTicket.data.UXDX_Product_Id,
      productName: selectedTicket.data.Product_Name,
      startDate: selectedTicket.data.Product_Start_Date,
      productTaxCountry: conference.data.ProductTaxCountry?.[0],
      orderId,
      uid: user && user.uid,
      email: (user && user.email) || email,
      inPersonCount: tickets.reduce((acc, cur) => acc + (cur.data.Delivery[0] === 'In-Person' ? 1 : 0), 0),
      onlineCount: tickets.reduce((acc, cur) => acc + (cur.data.Delivery[0] === 'Online' ? 1 : 0), 0),
      workshopCount: tickets.reduce(
        (acc, cur) => acc + (cur.data.Ticket_Type_Name.indexOf('Workshop') > 1 ? 1 : 0),
        0,
      ),
      organisation: conference.data.Organisation,
      currency: selectedTicket.data.Currency,
      sendInBlueIds: [selectedTicket.data.SendinBlue_Ticket_Id, selectedTicket.data.SendinBlue_Id].join(','),
      subscribe: subscribe ? sibIds.join(',') : false,
      sibIds: tickets.length === 1 ? (subscribe ? sibIds.join(',') : false) : sibIds.join(','),
      billingPO: billingPO,
    }
    if (totalPrice === 0) {
      try {
        // register the ticket (validation needed at this point as well)
        // console.log(requestData)
        const registered = await ApiGateway.registerFreeTicket(requestData)
        clearTimeout(timer1)
        if (registered.isFree) {
          navigate(
            `${getEventLink(conference.data.Conference_Id, conferenceLocation)}/success?env=${env}&confId=${
              conference.data.Conference_Id
            }&order_id=${orderId}`,
          )
        } else {
          setIsSubmitting('')
          Sentry.captureMessage('Error registering for free conference' + JSON.stringify(registered))
          setError('Something went wrong. If this happens again please let us know in the chat.')
        }
      } catch (e) {
        setIsSubmitting('')
        Sentry.captureMessage('Error registering for free conference' + e)
        setError('Something went wrong. Please try again later.')
      }
    } else {
      // let automatedTaxRequired
      const selectedTickets = allTickets
        .filter((ticket) => quantity[ticket.id] > 0)
        .map((ticket) => {
          const isEligibleForDiscount = quantity[ticket.id] >= 5
          const details: Details = {
            quantity: quantity[ticket.id],
            price:
              env.indexOf('prod') !== -1
                ? isEligibleForDiscount
                  ? ticket.data.Stripe_Reduced_Price_Id
                  : ticket.data.Stripe_Price_Id
                : isEligibleForDiscount
                  ? ticket.data.Stripe_Reduced_Price_Id__Test_
                  : ticket.data.Stripe_Price_Id__Test_,
          }
          return details
        })
      const baseUrl = env.indexOf('prod') !== -1 ? 'https://uxdx.com' : 'http://localhost:8000'

      const lineItems = selectedTickets.map((item) => ({
        ...item,
      }))
      if (selectedTicket.data.Delivery[0] === 'In-Person' && selectedTicket.data.Stripe_Tax_Id) {
        lineItems.forEach((lineItem) => {
          lineItem.tax_rates =
            env.indexOf('prod') !== -1
              ? selectedTicket.data.Stripe_Tax_Id
              : selectedTicket.data.Stripe_Tax_Id__Test_
        })
      }

      const sessionDetails: StripeInputDetails = {
        customer_email: (user && user.email) || email,
        metadata: {
          orderId,
          productType: selectedTicket.data.Product_Type,
          productId: selectedTicket.data.UXDX_Product_Id,
          productName: selectedTicket.data.Product_Name,
          productTimezone: conference.data.Timezone || '',
          startDate: selectedTicket.data.Product_Start_Date.split('T')[0],
          productTaxCountry: conference.data.ProductTaxCountry?.[0],
          organisation: conference.data.Organisation,
          sendInBlueIds: [selectedTicket.data.SendinBlue_Ticket_Id, selectedTicket.data.SendinBlue_Id].join(
            ',',
          ),
          subscribe: subscribe ? sibIds.join(',') : false,
          email: (user && user.email) || email,
          uid: user && user.uid,
          sibIds: tickets.length === 1 ? (subscribe ? sibIds.join(',') : false) : sibIds.join(','),
          billingPO: billingPO,
        },
        mode: 'payment',
        line_items: lineItems,
        // automatic_payment_methods:  { enabled: true },
        payment_method_types: ['card'],
        success_url:
          baseUrl +
          getEventLink(conference.data.Conference_Id, conferenceLocation) +
          '/success?env=' +
          env +
          '&confId=' +
          conference.data.Conference_Id +
          '&order_id=' +
          orderId +
          '&organisation=' +
          conference.data.Organisation +
          '&session_id={CHECKOUT_SESSION_ID}',
        cancel_url: baseUrl + getEventLink(conference.data.Conference_Id, conferenceLocation, 'tickets'),
        tax_id_collection: { enabled: true },
        allow_promotion_codes: true,
        billing_address_collection: 'required',
        after_expiration: {
          recovery: {
            enabled: true,
            allow_promotion_codes: true,
          },
        },
      }

      if (!selectedTicket.data.Stripe_Tax_Id && !selectedTicket.data.No_Tax[0]) {
        sessionDetails.automatic_tax = {
          enabled: true,
        }
      }
      // remove null values from sessionDetails
      const stripeCustomerId = user
        ? env.indexOf('prod') !== -1
          ? user[conference.data.Region + 'stripeCustomerId']
          : user[conference.data.Region + 'stripeCustomerIdTest']
        : null
      if (user && stripeCustomerId) {
        sessionDetails.customer = stripeCustomerId
        delete sessionDetails.customer_email
        sessionDetails.customer_update = {
          name: 'auto',
          address: 'auto',
        }
      }
      if (!sessionDetails.customer_email) delete sessionDetails.customer_email

      try {
        // else ask them to pay
        setIsSubmitting('Preparing your order')
        const timer1 = setTimeout(() => setIsSubmitting('Validating details'), 3500)
        const timer2 = setTimeout(() => setIsSubmitting('Redirecting to Stripe'), 6500)
        const details = await ApiGateway.handleStripePayment(sessionDetails, env)
        clearTimeout(timer1)
        clearTimeout(timer2)
        if (details.url) {
          window.location.href = details.url
        } else {
          setIsSubmitting('')
          Sentry.captureException(new Error('Stripe Checkout failed' + JSON.stringify(details)))
          setError('Something went wrong. If this happens again please let us know in the chat.')
        }
      } catch (e) {
        setIsSubmitting('')
        Sentry.captureException(e)
        setError('Something has gone wrong. If this happens again please let us know in the chat.')
      }
    }
  }

  return (
    <GenericModal isVisible={isVisible} onClose={onClose}>
      <div className="flex flex-1 flex-col justify-center px-4 pt-12 pb-12 sm:px-6 lg:flex-none lg:px-12 lg:pt-12">
        <div className="mx-auto w-full">
          <div>
            <h2 className="mb-6 text-3xl font-extrabold leading-9 text-gray-900">
              {conference.data.Conference_Name}
            </h2>
          </div>

          {!showBillingInfo && (
            <form onSubmit={(e) => onBuyClickHandler(e)}>
              {(!user || !user.email) && (
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email for Order Details
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MailIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
              )}
              <div className="mb-4">
                {Object.keys(groupedTickets)
                  .filter((delivery) => selectedTicket.data.Delivery?.[0] === delivery)
                  .map((ticketType) => {
                    const tickets = groupedTickets[ticketType]

                    if (!tickets) {
                      return null
                    }

                    return (
                      <div key={ticketType}>
                        <h3 className="mt-6 mb-4 text-2xl font-extrabold leading-9 text-gray-900">
                          {ticketType}
                        </h3>

                        {tickets
                          .filter((ticket) => ticket.Remaining_Tickets !== 0)
                          .map((ticket) => {
                            ticket.reducedPrice = ticket.Price * discount5for4
                            return (
                              <div key={ticket.id} className="mb-4 flex items-center justify-between">
                                <div className="w-40 lg:w-96">
                                  <h4 className="text-md mb-1 font-medium text-gray-900">
                                    {ticket.name + ':' + ticketType}
                                  </h4>
                                  <p className="text-xs text-gray-600">{ticket.Ticket_SubTitle}</p>
                                </div>
                                <div className="flex items-center">
                                  <p className="pl-2">
                                    <span
                                      className={`${
                                        discounts[ticket.id] < 1 ? 'line-through text-gray-400' : ''
                                      }`}
                                    >
                                      {ticket.Price.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: ticket.Currency,
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                    {discounts[ticket.id] < 1 && (
                                      <span className="ml-1">
                                        {ticket.reducedPrice.toLocaleString('en-US', {
                                          style: 'currency',
                                          currency: ticket.Currency,
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 2,
                                        })}
                                      </span>
                                    )}
                                  </p>
                                  <p className="px-1 text-gray-500">x</p>
                                  <div className="relative flex h-10 w-24 flex-row rounded-lg">
                                    <button
                                      type="button"
                                      disabled={ticket.Remaining_Tickets === 0}
                                      onClick={decreaseTicketQuantity(ticket.id)}
                                      className="flex h-full w-20 cursor-pointer text-3xl font-medium text-gray-500 hover:bg-gray-100 focus:outline-none"
                                    >
                                      <span className="m-auto">-</span>
                                    </button>
                                    <input
                                      type="hidden"
                                      disabled={ticket.Remaining_Tickets === 0}
                                      className="border-gray-400 p-1 text-center text-xs focus:outline-none md:p-2 md:text-base"
                                      readOnly
                                      name="custom-input-number"
                                    />
                                    <div className="flex w-24 cursor-default items-center justify-center rounded-md border border-gray-400 bg-white text-xs md:text-base">
                                      <span>{quantity[ticket.id]}</span>
                                    </div>

                                    <button
                                      type="button"
                                      disabled={ticket.Remaining_Tickets === 0}
                                      onClick={increaseTicketQuantity(ticket.id)}
                                      className="flex h-full w-20 cursor-pointer border-gray-400 text-3xl font-medium text-gray-500 hover:bg-gray-100 focus:outline-none"
                                    >
                                      <span className="m-auto">+</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                      </div>
                    )
                  })}
              </div>
              {error !== '' && (
                <div className="mt-3 rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium leading-5 text-red-800">{error}</h3>
                    </div>
                  </div>
                </div>
              )}

              <div className="pb-4 pt-4 mt-6 w-full border-t border-gray-200 flex justify-end">
                <div className="text-right">
                  {tax > 0 && (
                    <p className="text-base text-gray-500">
                      {selectedTicket.data.Tax_Rates?.[0]?.data?.Name}{' '}
                      {selectedTicket.data.Tax_Rates?.[0]?.data?.Rate}%:{' '}
                      {tax.toLocaleString('en-US', {
                        style: 'currency',
                        currency: currency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                  )}
                  <p className="text-lg">
                    <span className="font-bold">Total:</span>{' '}
                    {(totalPrice + tax).toLocaleString('en-US', {
                      style: 'currency',
                      currency: currency.toUpperCase(),
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })}{' '}
                    {!tax && <span className="text-sm text-gray-500">excluding taxes</span>}
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-center mb-4 mt-2 text-left">
                  <input
                    id="subscribe-events"
                    type="checkbox"
                    className="form-checkbox mr-4 h-4 w-4 text-indigo-600 border border-gray-300 rounded-sm transition duration-150 ease-in-out"
                    checked={subscribe} // Use the state value here
                    onChange={(e) => setSubscribe(e.target.checked)} // Update the state when checkbox is clicked
                  />
                  <label htmlFor="subscribe-events" className="ml-2 block text-xs leading-5 text-gray-600">
                    I agree to receive emails from UXDX
                  </label>
                </div>
              </div>

              <div>
                <span className="block w-full rounded-md shadow-sm">
                  {isSubmitting.length === 0 && (
                    <div className="flex flex-wrap sm:space-x-4">
                      <button
                        id={
                          conferenceLocation === 'community'
                            ? 'community-purchase-ticket-button'
                            : 'purchase-ticket-button'
                        }
                        type="submit"
                        className={`focus:shadow-outline-indigo flex w-full px-10 sm:w-auto justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white transition duration-150 ease-in-out hover:bg-indigo-500 focus:border-indigo-700 focus:outline-none active:bg-indigo-700`}
                      >
                        {totalPrice > 0 ? 'Pay by Card' : 'Register Now'}
                      </button>
                      {totalPrice > 0 && (
                        <button
                          id={
                            conferenceLocation === 'community'
                              ? 'community-invoice-request-button-start'
                              : 'invoice-request-button-start'
                          }
                          onClick={getBillingInfo}
                          className={`mt-4 sm:mt-0 focus:shadow-outline-indigo flex w-full sm:w-auto justify-center rounded-md border border-slate-900 py-2 px-4 text-sm font-medium text-gray-900  transition duration-150 ease-in-out hover:bg-gray-100 focus:border-indigo-700 focus:outline-none active:bg-gray-300`}
                        >
                          Pay by Bank Transfer
                        </button>
                      )}
                    </div>
                  )}
                  {isSubmitting.length > 0 && (
                    <div
                      className={`flex w-full justify-center rounded-md border border-transparent bg-indigo-400 py-2 px-4 text-sm font-medium text-white  transition duration-150 ease-in-out`}
                    >
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {isSubmitting}
                    </div>
                  )}
                </span>
              </div>
            </form>
          )}
          {showBillingInfo && (
            <form onSubmit={handleInvoiceRequest} className="mb-4">
              {(!user || !user.email) && (
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email for Order Details
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MailIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
              )}
              {/* <h2 className="text-2xl pb-6">Please enter your billing information</h2> */}
              <div className="pt-4">
                <label htmlFor="billingName" className="block text-sm font-medium text-gray-700">
                  Business Name
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="input"
                    name="billingName"
                    id="billingName"
                    value={billingName}
                    onChange={(e) => setBillingName(e.target.value)}
                    required
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm py-1 pl-2 border border-gray-300 rounded-md"
                    placeholder="Your business name"
                  />
                </div>
              </div>
              <label htmlFor="billingCountry" className="block text-sm font-medium text-gray-700 pt-4">
                Billing Address
              </label>

              <div>
                <label htmlFor="billingLine1" className="sr-only">
                  Address Line 1
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="input"
                    name="billingLine1"
                    id="billingLine1"
                    value={billingLine1}
                    onChange={(e) => setBillingLine1(e.target.value)}
                    required
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm py-1 pl-2 border border-gray-300 rounded-md"
                    placeholder="Address Line 1"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="billingLine2" className="sr-only">
                  Address Line 2
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="input"
                    name="billingLine2"
                    id="billingLine2"
                    value={billingLine2}
                    onChange={(e) => setBillingLine2(e.target.value)}
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm py-1 pl-2 border border-gray-300 rounded-md"
                    placeholder="Address Line 2"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="billingCity" className="sr-only">
                  City
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="input"
                    name="billingCity"
                    id="billingCity"
                    value={billingCity}
                    onChange={(e) => setBillingCity(e.target.value)}
                    required
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm py-1 pl-2 border border-gray-300 rounded-md"
                    placeholder="City"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="billingState" className="sr-only">
                  State
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="input"
                    name="billingState"
                    id="billingState"
                    value={billingState}
                    onChange={(e) => setBillingState(e.target.value)}
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm py-1 pl-2 border border-gray-300 rounded-md"
                    placeholder="State"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="billingPostalCode" className="sr-only">
                  Postal Code
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="input"
                    name="billingPostalCode"
                    id="billingPostalCode"
                    value={billingPostalCode}
                    onChange={(e) => setBillingPostalCode(e.target.value)}
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm py-1 pl-2 border border-gray-300 rounded-md"
                    placeholder="Postal Code"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="billingCountry" className="sr-only">
                  Country
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <CountryDropdown
                    value={billingCountry}
                    valueType="short"
                    onChange={(val) => setBillingCountry(val)}
                    classes="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm py-1 pl-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="billingTaxId" className="block text-sm font-medium text-gray-700 pt-4">
                  Tax Id
                </label>
                <div className="mt-1 relative rounded-md shadow-sm flex space-x-4">
                  <select
                    id="billingTaxType"
                    name="billingTaxType"
                    value={billingTaxType}
                    onChange={(e) => {
                      setBillingTaxType(e.target.value)
                      getPlaceholder(e.target.value)
                    }}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md text-gray-500"
                    placeholder="Tax Type"
                  >
                    <option value="" selected className="">
                      Pick a type
                    </option>
                    {taxIdTypes.map((type) => {
                      return (
                        <option key={type.id} value={type.id}>
                          {type.flag} {type.name}
                        </option>
                      )
                    })}
                  </select>
                  <input
                    type="input"
                    name="billingTaxId"
                    id="billingTaxId"
                    value={billingTaxId}
                    onChange={(e) => setBillingTaxId(e.target.value)}
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm py-1 pl-2 border border-gray-300 rounded-md"
                    placeholder={billingTaxPlaceholder}
                  />
                </div>
                {/* <div>
                  <label htmlFor="billingPO" className="block text-sm font-medium text-gray-700 pt-4">
                    PO number
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <input
                      type="input"
                      name="billingPO"
                      id="billingPO"
                      value={billingPO}
                      onChange={(e) => setBillingPO(e.target.value)}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm py-1 pl-2 border border-gray-300 rounded-md"
                      placeholder="PO Number"
                    />
                  </div>
                </div> */}
              </div>

              <div className="py-6">
                {hideDiscount && (
                  <p onClick={() => setHideDiscount(false)} className="text-blue-800 text-sm cursor-pointer">
                    Add discount code
                  </p>
                )}
                {!hideDiscount && (
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Discount Code
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <input
                        type="text"
                        name="discount"
                        id="discount"
                        value={discount}
                        onChange={(e) => setDiscount(e.target.value)}
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="pb-8 pt-4 mt-6 w-full border-t border-gray-200 flex justify-end">
                <div className="text-right">
                  {tax > 0 && (
                    <p className="text-base text-gray-500">
                      {selectedTicket.data.Tax_Rates?.[0]?.data?.Name}{' '}
                      {selectedTicket.data.Tax_Rates?.[0]?.data?.Rate}%:{' '}
                      {tax.toLocaleString('en-US', {
                        style: 'currency',
                        currency: currency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                  )}
                  <p className="text-lg">
                    <span className="font-bold">Total:</span>{' '}
                    {(totalPrice + tax).toLocaleString('en-US', {
                      style: 'currency',
                      currency: currency.toUpperCase(),
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })}{' '}
                    {!tax && <span className="text-sm text-gray-500">excluding taxes</span>}
                  </p>
                </div>
              </div>
              {!tax && (
                <p className="text-sm text-gray-500">
                  Taxes calculated based on products, region of purchase, and company tax status.
                </p>
              )}

              {error !== '' && (
                <div className="mt-3 rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium leading-5 text-red-800">{error}</h3>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap sm:space-x-4 pt-8">
                {isRequestingInvoice.length === 0 && (
                  <>
                    <button
                      id={
                        conferenceLocation === 'community'
                          ? 'community-invoice-request-button'
                          : 'invoice-request-button'
                      }
                      type="submit"
                      className={`focus:shadow-outline-indigo flex w-full px-10 sm:w-auto justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white  transition duration-150 ease-in-out hover:bg-indigo-500 focus:border-indigo-700 focus:outline-none active:bg-indigo-700`}
                    >
                      Request Invoice
                    </button>
                    <button
                      onClick={() => setShowBillingInfo(false)}
                      className={`mt-4 sm:mt-0 focus:shadow-outline-indigo flex w-full sm:w-auto justify-center rounded-md border border-slate-900 py-2 px-4 text-sm font-medium text-gray-900  transition duration-150 ease-in-out hover:bg-gray-100 focus:border-indigo-700 focus:outline-none active:bg-gray-300`}
                    >
                      Back
                    </button>
                  </>
                )}
                {isRequestingInvoice.length > 0 && (
                  <div
                    className={`flex w-full justify-center rounded-md border border-transparent bg-indigo-400 py-2 px-4 text-sm font-medium text-white  transition duration-150 ease-in-out`}
                  >
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {isRequestingInvoice}
                  </div>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </GenericModal>
  )
}

export default PurchaseTickets
